import React from 'react';
import { portfolioData } from '../data/mock';

/**
 * Single-page A4 resume artifact.
 * Hidden on screen, revealed only in @media print so window.print() yields
 * a clean one-page CV. Layout follows /app/design_guidelines.json
 * (Swiss / high-contrast, IBM Plex Sans, 12-col grid w/ 4-col sidebar).
 */
const ResumeArtifact = () => {
  const { personal, experience, skills, projects, openSource, education } = portfolioData;

  const linkedinShort = personal.linkedIn.replace(/^https?:\/\/(www\.)?/, '');

  const skillCategories = [
    { label: 'Cloud', items: skills.cloud },
    { label: 'Orchestration', items: skills.orchestration },
    { label: 'Observability', items: skills.observability },
    { label: 'IaC', items: skills.iac },
    { label: 'Automation', items: skills.automation },
    { label: 'Tools', items: skills.tools },
  ];

  return (
    <div
      className="print-only-resume hidden print:block bg-white text-slate-900"
      data-testid="resume-artifact"
      style={{ fontFamily: "'IBM Plex Sans', system-ui, -apple-system, sans-serif" }}
    >
      {/* ============== HEADER ============== */}
      <header
        data-testid="resume-header"
        className="flex items-end justify-between border-b border-slate-300 pb-2 mb-3"
      >
        <div>
          <h1 className="text-2xl font-black tracking-tighter text-slate-900 leading-none">
            {personal.name}
          </h1>
          <p className="text-[11px] font-semibold text-slate-700 mt-1 tracking-tight">
            {personal.title}
          </p>
          <p className="text-[8.5px] text-slate-500 mt-0.5">{personal.tagline}</p>
        </div>
        <ul className="text-[8.5px] text-slate-700 text-right space-y-[1px] leading-tight">
          <li>{personal.email}</li>
          <li>{personal.location}</li>
          <li>{linkedinShort}</li>
          <li>tilakraj.cv</li>
        </ul>
      </header>

      {/* ============== BODY GRID ============== */}
      <div className="grid grid-cols-12 gap-4">
        {/* ---- SIDEBAR (4 cols) ---- */}
        <aside className="col-span-4 space-y-3">
          {/* Skills */}
          <section data-testid="resume-skills">
            <h2 className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-800 border-b border-slate-300 pb-0.5 mb-1.5">
              Skills
            </h2>
            <ul className="space-y-1">
              {skillCategories.map((cat) => (
                <li key={cat.label} className="text-[8.5px] text-slate-700 leading-snug">
                  <span className="font-bold text-slate-900">{cat.label}: </span>
                  {cat.items.join(', ')}
                </li>
              ))}
            </ul>
          </section>

          {/* Projects */}
          <section data-testid="resume-projects">
            <h2 className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-800 border-b border-slate-300 pb-0.5 mb-1.5">
              Selected Projects
            </h2>
            <ul className="space-y-1.5">
              {projects.map((p) => (
                <li key={p.id} className="text-[8.5px] leading-snug">
                  <div className="font-bold text-slate-900">{p.title}</div>
                  <div className="text-slate-600">{p.description}</div>
                  <div className="text-[7.5px] text-slate-500 mt-0.5 italic">
                    {p.technologies.slice(0, 4).join(' · ')}
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Open Source */}
          <section data-testid="resume-opensource">
            <h2 className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-800 border-b border-slate-300 pb-0.5 mb-1.5">
              Open Source
            </h2>
            <ul className="space-y-1.5">
              {openSource.map((o) => (
                <li key={o.id} className="text-[8.5px] leading-snug">
                  <div className="font-bold text-slate-900">{o.project}</div>
                  <div className="text-slate-600">{o.description}</div>
                  <div className="text-[7.5px] text-slate-500 mt-0.5 italic">
                    {(o.tags || []).join(' · ')}
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Education */}
          <section data-testid="resume-education">
            <h2 className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-800 border-b border-slate-300 pb-0.5 mb-1.5">
              Education
            </h2>
            <div className="text-[8.5px] leading-snug">
              <div className="font-bold text-slate-900">{education.degree}</div>
              <div className="text-slate-700">{education.institution}</div>
              <div className="text-[7.5px] text-slate-500">
                {education.location} · {education.period}
              </div>
            </div>
          </section>
        </aside>

        {/* ---- MAIN (8 cols) ---- */}
        <main className="col-span-8 space-y-3">
          {/* Summary */}
          <section data-testid="resume-summary">
            <h2 className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-800 border-b border-slate-300 pb-0.5 mb-1.5">
              Summary
            </h2>
            <p className="text-[9px] leading-snug text-slate-700">{personal.summary}</p>
          </section>

          {/* Experience */}
          <section data-testid="resume-experience">
            <h2 className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-800 border-b border-slate-300 pb-0.5 mb-1.5">
              Experience
            </h2>
            <div className="space-y-2.5">
              {experience.map((job, idx) => {
                const useTwoCols = idx === 0; // Emergent (longest) → 2-col bullets
                return (
                  <div key={job.id} className="text-[8.5px] leading-snug">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="min-w-0">
                        <span className="font-bold text-slate-900">{job.role}</span>
                        <span className="text-slate-600"> — {job.company}</span>
                      </div>
                      <div className="text-[7.5px] text-slate-500 whitespace-nowrap">
                        {job.location} · {job.period}
                      </div>
                    </div>
                    <ul
                      className={
                        useTwoCols
                          ? 'grid grid-cols-2 gap-x-3 gap-y-[1px] mt-0.5 list-disc pl-3'
                          : 'mt-0.5 list-disc pl-3 space-y-[1px]'
                      }
                    >
                      {job.achievements.map((a, i) => (
                        <li key={i} className="text-slate-700 leading-snug marker:text-slate-400">
                          {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default ResumeArtifact;
